'use client';

import { useCallback, useRef, type FocusEvent, type KeyboardEvent, type RefObject } from 'react';

interface UsePickupPointFocusGuardOptions {
  selectorRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

/** Stops the combobox from reopening when focus returns to it after closing or tabbing away. */
export function usePickupPointFocusGuard({ selectorRef, isOpen, setIsOpen }: UsePickupPointFocusGuardOptions) {
  const suppressFocusOpenAfterClose = useRef(false);

  const focusTriggerAfterClose = useCallback(() => {
    suppressFocusOpenAfterClose.current = true;

    requestAnimationFrame(() => {
      selectorRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    });
  }, [selectorRef]);

  const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    if (!suppressFocusOpenAfterClose.current) {
      return;
    }

    suppressFocusOpenAfterClose.current = false;
    setIsOpen(false);
    event.stopPropagation();
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && isOpen) {
      suppressFocusOpenAfterClose.current = true;

      // The browser moves focus synchronously after keydown. If it left the selector, the focus handler never
      // consumes the flag and it would swallow the next legitimate focus-open.
      requestAnimationFrame(() => {
        suppressFocusOpenAfterClose.current = false;
      });
    }
  };

  return { focusTriggerAfterClose, handleFocusCapture, handleKeyDownCapture };
}
